import { union } from 'ramda';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  DocumentResourceType,
  selectDocumentTasksContentMap,
} from 'data/documents';
import { selectAllDocumentResourceSections } from 'data/templates/documents';

export const useRocketlaneDocumentTasks = ({ docId }: { docId: number }) => {
  const tasksDocMap = useSelector(selectDocumentTasksContentMap);
  const resourceSections = useSelector(
    selectAllDocumentResourceSections(docId),
  );

  const defaultResourceSections = useMemo(
    () =>
      resourceSections.filter(
        (resourceSection) =>
          resourceSection.resourceType === DocumentResourceType.Default,
      ),
    [resourceSections],
  );

  const defaultResourceSection = defaultResourceSections[0];

  const highlightResourceSections = useMemo(
    () =>
      resourceSections.filter(
        (resourceSection) =>
          resourceSection.resourceType === DocumentResourceType.Highlight,
      ),
    [resourceSections],
  );
  const nonOrphanTasks = useMemo(
    () =>
      highlightResourceSections
        .filter((section) => {
          return (
            tasksDocMap[section.resourceSectionId] &&
            tasksDocMap[section.resourceSectionId].isActive
          );
        })
        .map((s) => {
          return s.resourceObjects?.map((t) => {
            return {
              ...t,
              resourceSectionId: s.resourceSectionId,
              resourceType: s.resourceType,
            };
          });
        })
        .flat(),
    [highlightResourceSections, tasksDocMap],
  );
  const orphanTasks = useMemo(
    () =>
      highlightResourceSections
        .filter((section) => {
          return (
            (tasksDocMap[section.resourceSectionId] &&
              !tasksDocMap[section.resourceSectionId].isActive) ||
            !tasksDocMap[section.resourceSectionId]
          );
        })
        .map((s) => {
          return s.resourceObjects?.map((t) => {
            return {
              ...t,
              resourceSectionId: s.resourceSectionId,
              resourceType: s.resourceType,
            };
          });
        })
        .flat(),
    [highlightResourceSections, tasksDocMap],
  );
  const defaultResourceSectionTasks = useMemo(
    () =>
      defaultResourceSections
        .map((s) => {
          return s.resourceObjects?.map((t) => {
            return {
              ...t,
              resourceSectionId: s.resourceSectionId,
              resourceType: s.resourceType,
            };
          });
        })
        .flat(),
    [defaultResourceSections],
  );
  const documentTasks = useMemo(
    () => union(defaultResourceSectionTasks, nonOrphanTasks),
    [defaultResourceSectionTasks, nonOrphanTasks],
  );

  return {
    documentTasks,
    orphanTasks,
    defaultResourceSection,
  };
};
